import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  QueryList,
  signal,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { ControlContainer, NgForm } from '@angular/forms';
import { MatTab, MatTabGroup, MatTabHeader } from '@angular/material/tabs';
import { CompanyStateService } from 'src/app/auth/services/company-state.service';
import { UserStateService } from 'src/app/auth/services/user-state.service';
import { TrackerService } from 'src/app/components/logging/tracker.service';
import { ModalService } from 'src/app/components/modals/modal.service';
import { FeaturesService } from 'src/app/components/plans/features.service';
import { UpgradeNoticeComponent } from 'src/app/components/plans/upgrade-notice/upgrade-notice.component';
import { DisplayControlService } from 'src/app/displays/services/display-control.service';
import { PlayerActionsService } from 'src/app/displays/services/player-actions.service';
import { PlayerLicenseService } from 'src/app/displays/services/player-license.service';
import { PlayerProService } from 'src/app/displays/services/player-pro.service';
import { BroadcasterService } from 'src/app/shared/services/broadcaster.service';
import { ClipboardService } from 'src/app/shared/services/clipboard.service';
import { SnackbarService } from 'src/app/shared/services/snackbar.service';
import { CryptoUtilsService } from 'src/app/shared/services/crypto-utils.service';
import { environment } from 'src/environments/environment';
import { DisplayService } from '../../services/display.service';

const DEFAULT_VOLUME_PERCENTAGE = 75;

/** Definition for a room-mapping integration (id, name, logo, optional role). */
export interface IntegrationDefinition {
  id: string;
  name: string;
  logo: string;
  requireRole?: string;
}

interface ManualActionOption {
  type?: 'action' | 'divider';
  value: string;
  label: string;
  disabled: boolean;
  requiresSaRole?: boolean;
  enabledOffline?: boolean;
}

/** Static definitions for room-mapping integrations. */
const INTEGRATION_DEFINITIONS: ReadonlyArray<IntegrationDefinition> = [
  {
    id: 'adastra',
    name: 'Ad Astra',
    logo: 'images/logo-ad-astra.png',
    requireRole: 'sa',
  },
  // {
  //   id: 'accruent',
  //   name: 'Accruent EMS',
  //   logo: 'images/logo-accruent.png',
  // },
  {
    id: 'ical',
    name: 'iCal',
    logo: 'images/icon-link.svg',
  },
];

@Component({
  selector: 'display-fields',
  templateUrl: './display-fields.component.html',
  viewProviders: [{ provide: ControlContainer, useExisting: NgForm }],
  styleUrls: ['./display-fields.component.scss'],
  standalone: false,
})
export class DisplayFieldsComponent implements OnInit, AfterViewInit {
  @Input() selectedSchedule: any = {};
  @Input() displayControlType: string;
  @Output() selectedScheduleChange: EventEmitter<any> = new EventEmitter<any>();
  @Output() displayControlTypeChange: EventEmitter<string> =
    new EventEmitter<string>();
  @ViewChildren('UpgradeNotice') notices: QueryList<UpgradeNoticeComponent>;
  @ViewChild('TabGroup') tabGroup: MatTabGroup;

  isMonitoringAvailable = this.featuresService.isFeatureAvailable(
    FeaturesService.FEATURE_MONITORING,
  );
  isDisplayControlAvailable = this.featuresService.isFeatureAvailable(
    FeaturesService.FEATURE_DISPLAY_CONTROL,
  );
  isClassroomAlertsAvailable = this.featuresService.isFeatureAvailable(
    FeaturesService.FEATURE_CLASSROOM_ALERTS,
  );
  isScreenSharingAvailable = this.featuresService.isFeatureAvailable(
    FeaturesService.FEATURE_SCREEN_SHARING,
  );
  isScreensaverAvailable = this.featuresService.isFeatureAvailable(
    FeaturesService.FEATURE_SCREENSAVER,
  );
  hasInteractiveLicensing = signal(
    this.playerLicenseService.hasInteractiveLicenses(),
  );
  get isVolumeControlAvailable() {
    return this.playerProService.isVolumeControlCompatiblePlayer(
      this.displayService.display,
    );
  }

  get isKeyOperationModeDeviceAvailable() {
    return this.playerProService.isKeyOperationModeDeviceCompatiblePlayer(
      this.displayService.display,
    );
  }

  get keyOperationModeDevice() {
    return (
      this.displayService.display?.settings?.keyOperationModeDevice ===
      PlayerActionsService.KEY_OPERATION_MODE_TYPES.BLOCK_ALL
    );
  }

  set keyOperationModeDevice(value: boolean) {
    if (this.displayService.display?.settings) {
      this.displayService.display.settings.keyOperationModeDevice = value
        ? PlayerActionsService.KEY_OPERATION_MODE_TYPES.BLOCK_ALL
        : PlayerActionsService.KEY_OPERATION_MODE_TYPES.NORMAL;
    }
  }

  get volumePercentage() {
    this.ensureVolumeControlSettings();
    const value = this.displayService.display?.settings?.volumePercentage;
    if (typeof value === 'string') {
      return parseInt(value, 10) || DEFAULT_VOLUME_PERCENTAGE;
    }
    return value || DEFAULT_VOLUME_PERCENTAGE;
  }

  set volumePercentage(value: number) {
    this.ensureVolumeControlSettings();
    if (this.displayService.display?.settings) {
      this.displayService.display.settings.volumePercentage = Math.max(
        0,
        Math.min(100, value),
      );
    }
  }
  showWebPlayer = false;
  timePickerKey = false;
  displayForm: NgForm;
  selectedTabIndex = 0;
  selectedManualAction = PlayerActionsService.ACTION_TYPES.REBOOT;
  isSendingManualAction = false;

  get manualActionOptions(): ManualActionOption[] {
    const display = this.displayService.display;
    const isScreenPowerCompatible =
      this.playerProService.isScreenPowerOnCompatiblePlayer(display);

    return [
      {
        type: 'action',
        value: PlayerActionsService.ACTION_TYPES.REBOOT,
        label: 'Reboot media player',
        disabled: !this.playerProService.isRebootCompatiblePlayer(display),
        enabledOffline: false,
      },
      {
        type: 'divider',
        value: '__divider_player_actions__',
        label: '',
        disabled: false,
      },
      {
        type: 'action',
        value: PlayerActionsService.ACTION_TYPES.RESTART,
        label: 'Restart Rise Player',
        disabled: !this.playerProService.isRestartCompatiblePlayer(display),
        enabledOffline: false,
      },
      {
        type: 'action',
        value: PlayerActionsService.ACTION_TYPES.LAUNCH,
        label: 'Launch Rise Player',
        disabled: !this.playerProService.isRestartCompatiblePlayer(display),
        requiresSaRole: true,
        enabledOffline: true,
      },
      {
        type: 'divider',
        value: '__divider_screen_power__',
        label: '',
        disabled: false,
      },
      {
        type: 'action',
        value: PlayerActionsService.ACTION_TYPES.POWER_ON,
        label: 'Turn on screen',
        disabled: !isScreenPowerCompatible,
        enabledOffline: false,
      },
      {
        type: 'action',
        value: PlayerActionsService.ACTION_TYPES.POWER_OFF,
        label: 'Turn off screen',
        disabled: !isScreenPowerCompatible,
        enabledOffline: false,
      },
    ];
  }

  get visibleManualActionOptions(): ManualActionOption[] {
    const options = this.manualActionOptions;

    const filtered = options.filter((option, index) => {
      if (option.type === 'divider') {
        const hasEnabledBefore = options
          .slice(0, index)
          .some((o) => o.type !== 'divider' && !o.disabled);
        const hasEnabledAfter = options
          .slice(index + 1)
          .some((o) => o.type !== 'divider' && !o.disabled);
        return hasEnabledBefore && hasEnabledAfter;
      }

      return (
        !option.disabled &&
        (option.enabledOffline ||
          this.displayService.display.onlineStatus === 'online')
      );
    });

    const collapsed = filtered.reduce<ManualActionOption[]>((acc, option) => {
      if (option.type === 'divider') {
        const last = acc[acc.length - 1];
        if (last && last.type !== 'divider') {
          acc.push(option);
        }
        return acc;
      }
      acc.push(option);
      return acc;
    }, []);

    if (collapsed[collapsed.length - 1]?.type === 'divider') {
      collapsed.pop();
    }

    return collapsed;
  }

  get selectedManualActionLabel(): string {
    return (
      this.visibleManualActionOptions.find(
        (a) => a.type !== 'divider' && a.value === this.selectedManualAction,
      )?.label ?? ''
    );
  }

  private _screenId;
  get screenId() {
    if (!this._showScreenId()) {
      return undefined;
    } else if (!this._screenId && this.displayService.display.id) {
      this._screenId = this.displayService.display.id;
      this._generateScreenId();
    }

    return this._screenId;
  }

  static readonly SCREEN_ID_COMPANIES = [
    'f114ad26-949d-44b4-87e9-8528afc76ce4', // Rise Vision Production
    'b428b4e8-c8b9-41d5-8a10-b4193c789443', // Rise Vision Staging
    '15839198-3483-41b5-ac34-e5f6959b6cb3', // Jenkins company
    'a83b6404-f4f5-400c-830c-4a9fb6c3d4f1', // Customer (Caldwell University)
    'c80f8df6-f755-480e-a2e5-deea25bd551d', // Customer (St. Thomas Aquinas College)
    '14882e57-688a-46dc-babe-6ec2097fbfd0', // VenuEdge
    '2a40a480-ae66-4f53-a73f-0e01d8744c9b', // VenuEdge
  ];

  integrations: { id: string; name: string; logo: string; roomId: string }[] =
    [];

  constructor(
    public userStateService: UserStateService,
    public companyStateService: CompanyStateService,
    public playerActionsService: PlayerActionsService,
    public playerLicenseService: PlayerLicenseService,
    public playerProService: PlayerProService,
    public displayService: DisplayService,
    private displayControlService: DisplayControlService,
    private featuresService: FeaturesService,
    private modalService: ModalService,
    private clipboardService: ClipboardService,
    private snackbarService: SnackbarService,
    broadcasterService: BroadcasterService,
    private trackerService: TrackerService,
    private controlContainer: ControlContainer,
    private cryptoUtilsService: CryptoUtilsService,
    private changeDetectorRef: ChangeDetectorRef,
  ) {
    this.initIntegrations(INTEGRATION_DEFINITIONS);

    broadcasterService.on('risevision.company.updated', () => {
      const company = companyStateService.getCopyOfSelectedCompany(true);
      displayService.display.playerProAuthorized =
        displayService.display.playerProAuthorized ||
        (company.playerProAvailableLicenseCount > 0 &&
          displayService.display.playerProAssigned);
    });
  }

  initIntegrations(
    integrationDefinitions: ReadonlyArray<IntegrationDefinition>,
  ): void {
    this.integrations = integrationDefinitions
      .filter(
        (def) =>
          !def.requireRole || this.userStateService.hasRole(def.requireRole),
      )
      .map((def) => ({ ...def, roomId: '' }));
  }

  ngOnInit(): void {
    this.displayForm = <NgForm>this.controlContainer;
    this.ensureVolumeControlSettings();
  }

  /**
   * Called by parent component after display data has been loaded
   */
  onDisplayDataLoaded(): void {
    this.ensureVolumeControlSettings();
    this.loadIntegrationsFromSettings();
    this.syncManualActionSelection();
  }

  private syncManualActionSelection(): void {
    const shown = this.visibleManualActionOptions;
    if (shown.length === 0) {
      return;
    }
    const selectable = shown.filter((o) => o.type !== 'divider');
    if (selectable.length === 0) {
      return;
    }
    if (!selectable.some((o) => o.value === this.selectedManualAction)) {
      this.selectedManualAction = selectable[0].value;
    }
  }

  private ensureVolumeControlSettings() {
    if (!this.displayService.display) {
      return;
    }

    if (!this.displayService.display.settings) {
      this.displayService.display.settings = {};
    }

    if (
      this.displayService.display.settings.volumeControlSupported === undefined
    ) {
      this.displayService.display.settings.volumeControlSupported = false;
    }
    if (this.displayService.display.settings.volumePercentage === undefined) {
      this.displayService.display.settings.volumePercentage =
        DEFAULT_VOLUME_PERCENTAGE;
    }
  }

  ngAfterViewInit(): void {
    UpgradeNoticeComponent.hidePopovers(this.notices);
    this.tabGroup._handleClick = this.onTabClick.bind(this);

    // Ensure volume control settings are initialized after view is ready
    this.ensureVolumeControlSettings();
  }

  private readonly SCREEN_SHARING_TAB_INDEX = 2;

  private canSelectTab(index: number): boolean {
    return (
      index !== this.SCREEN_SHARING_TAB_INDEX ||
      this.isScreenSharingAvailable ||
      !this.isScreenSharingVisible
    );
  }

  private onTabClick(tab: MatTab, header: MatTabHeader, index: number): void {
    if (this.canSelectTab(index)) {
      this.selectedTabIndex = Math.max(0, index);
    } else {
      this.featuresService.showUpgradePlanModal(
        FeaturesService.FEATURE_SCREEN_SHARING,
      );
    }
  }

  scheduleSelected() {
    this.selectedScheduleChange.emit(this.selectedSchedule);

    this.displayService.confirmAssignLicense(this.selectedSchedule);
  }

  confirmLicensing() {
    return this.modalService
      .confirm(
        'Assign license?',
        'Do you want to assign one of your licenses to this display?',
        'Yes',
        'No',
      )
      .then(() => {
        // Toggle license as if they clicked the checkbox
        this.displayService.display.playerProAuthorized = true;

        this.displayService.toggleProAuthorized();
      });
  }

  getWebPlayerUrl() {
    if (!this.displayService.display) {
      return '';
    }
    return (
      environment.VIEWER_URL +
      '/?type=webplayer&id=' +
      this.displayService.display.id
    );
  }

  copyToClipboard(text: string): Promise<void> {
    return this.clipboardService.copy(text);
  }

  openTimePicker($event) {
    $event.preventDefault();
    $event.stopPropagation();
  }

  configureDisplayControl() {
    this.displayControlService.openDisplayControlModal(false);
  }

  toggleDisplayControl() {
    this.displayControlService.toggleDisplayControl();
  }

  trackDisplayMonitoring() {
    this.trackerService.displayEvent(
      'Display Monitoring Updated',
      this.displayService.display.id,
      this.displayService.display.name,
      {
        monitoringEnabled: this.displayService.display.monitoringEnabled,
      },
    );

    if (
      this.displayService.display.monitoringEnabled &&
      this.displayService.display.settings &&
      this.displayService.display.settings.setAsScreenSaver
    ) {
      this.displayService.display.settings.setAsScreenSaver = false;
    }
  }

  toggleScreensaver() {
    if (!this.isScreensaverAvailable) {
      this.promptUpgrade(FeaturesService.FEATURE_SCREENSAVER);
      setTimeout(() => {
        this.displayService.display.settings.setAsScreenSaver = false;
      }, 100);
      return;
    }

    if (
      this.displayService.display.settings.setAsScreenSaver &&
      this.displayService.display.monitoringEnabled
    ) {
      this.displayService.display.monitoringEnabled = false;
    }
  }

  promptUpgrade(feature) {
    this.featuresService.showUpgradePlanModal(feature);
  }

  async sendManualAction() {
    if (!this.canRunSelectedManualAction() || this.isSendingManualAction) {
      return;
    }

    const selectedLabel = this.selectedManualActionLabel;
    const actionType = this.selectedManualAction;
    this.isSendingManualAction = true;
    try {
      if (this.shouldBlockScreenPowerForInactiveDisplayControl()) {
        this.snackbarService.show({
          role: 'error',
          text: 'This display needs to have Display Control turned on and configured.',
          duration: 'long',
        });
        return;
      }

      const result = await this.playerActionsService.confirmWithResult(
        this.displayService.display.id,
        this.displayService.display.name,
        actionType,
      );

      switch (result.status) {
        case 'success':
          if (selectedLabel) {
            this.snackbarService.show({
              role: 'success',
              text: `Command Sent: ${selectedLabel}`,
              duration: 'short',
            });
          }
          break;
        case 'error':
          this.snackbarService.show({
            role: 'error',
            text: result.message,
            duration: 'long',
          });
          break;
        case 'cancelled':
        case 'license_required':
          break;
      }
    } finally {
      this.isSendingManualAction = false;
    }
  }

  private shouldBlockScreenPowerForInactiveDisplayControl(): boolean {
    if (!this.isManualActionScreenPower(this.selectedManualAction)) {
      return false;
    }
    const display = this.displayService.display;
    if (!this.playerProService.isDisplayControlCompatiblePlayer(display)) {
      return false;
    }
    return display?.isDisplayControlEnabled !== true;
  }

  private isManualActionScreenPower(action: string): boolean {
    return (
      action === PlayerActionsService.ACTION_TYPES.POWER_ON ||
      action === PlayerActionsService.ACTION_TYPES.POWER_OFF
    );
  }

  isManualActionAvailable(): boolean {
    if (!this.userStateService.hasRole('da')) {
      return false;
    }

    const display = this.displayService.display;
    if (!display || !display.playerVersion) {
      return false;
    }

    return this.visibleManualActionOptions.length > 0;
  }

  canRunSelectedManualAction(): boolean {
    if (!this.userStateService.hasRole('da')) {
      return false;
    }

    const display = this.displayService.display;
    if (!display || !display.playerVersion) {
      return false;
    }

    if (
      this.selectedManualAction === PlayerActionsService.ACTION_TYPES.REBOOT
    ) {
      return this.playerProService.isRebootCompatiblePlayer(display);
    }

    if (
      this.selectedManualAction === PlayerActionsService.ACTION_TYPES.RESTART ||
      this.selectedManualAction === PlayerActionsService.ACTION_TYPES.LAUNCH
    ) {
      return this.playerProService.isRestartCompatiblePlayer(display);
    }

    if (
      this.selectedManualAction ===
        PlayerActionsService.ACTION_TYPES.POWER_ON ||
      this.selectedManualAction === PlayerActionsService.ACTION_TYPES.POWER_OFF
    ) {
      return this.playerProService.isScreenPowerOnCompatiblePlayer(display);
    }

    return false;
  }

  onVolumeControlToggle(isEnabled: boolean) {
    if (!this.isVolumeControlAvailable) {
      return;
    }

    // Initialize volume control settings if they don't exist
    if (!this.displayService.display.settings) {
      this.displayService.display.settings = {};
    }

    // Update volume control support (ngModel already handled the toggle)
    this.displayService.display.settings.volumeControlSupported = isEnabled;

    // If disabling volume control, reset volume percentage
    if (!isEnabled) {
      this.displayService.display.settings.volumePercentage = 0;
    } else {
      // If enabling volume control, set default volume if not already set
      if (
        this.displayService.display.settings.volumePercentage === undefined ||
        this.displayService.display.settings.volumePercentage === 0
      ) {
        this.displayService.display.settings.volumePercentage =
          DEFAULT_VOLUME_PERCENTAGE;
      }
    }
  }

  onVolumeChange(volume: number) {
    this.volumePercentage = volume;
  }

  onVolumeInputChange(event: Event) {
    const target = event.target as HTMLInputElement;
    const volume = parseInt(target.value, 10);

    if (!isNaN(volume)) {
      this.volumePercentage = volume;
    }
  }

  getResolution() {
    if (
      this.playerProService.isAvocorRSeriesDisplay(this.displayService.display)
    ) {
      return '3840x2160';
    }
    return `${this.displayService.display.width}x${this.displayService.display.height}`;
  }

  detectChanges(): void {
    this.changeDetectorRef.detectChanges();
    this.displayControlTypeChange.emit(this.displayControlType);
    if (this.displayControlType === 'hdmi-cec') {
      this.displayControlService.setDisplayControlToCec();
    }
    if (this.displayControlType === 'rs-232') {
      this.playerProService.isGiadaRecommendedProduct(
        this.displayService.display,
      ) ||
      this.playerProService.isChromeOSPwaPlayer(this.displayService.display)
        ? this.displayControlService.openDisplayControlModal()
        : this.displayControlService.setDisplayControlToRs232();
    }
  }

  private get isPlayerVersionSetOrOnline(): boolean {
    return (
      Boolean(this.displayService.display.playerVersion) ||
      this.displayService.display.onlineStatus === 'online'
    );
  }

  protected get shouldShowRotationOption() {
    return (
      this.isPlayerVersionSetOrOnline &&
      this.playerProService.isDeviceOrientationCompatiblePlayer(
        this.displayService.display,
      )
    );
  }

  protected get isMonitoringEnabledAndAvailable(): boolean {
    return (
      !this.displayService.showLicenseRequired() &&
      this.displayService.display.monitoringEnabled &&
      this.isMonitoringAvailable
    );
  }

  protected get isScreenSharingVisible(): boolean {
    return this.playerProService.isScreenSharingCompatiblePlayer(
      this.displayService.display,
    );
  }

  _showScreenId(): boolean {
    return DisplayFieldsComponent.SCREEN_ID_COMPANIES.includes(
      this.companyStateService.getSelectedCompanyId(),
    );
  }

  async _generateScreenId() {
    const companyId = this.companyStateService.getSelectedCompanyId();
    const displayId = this.displayService.display.id;
    const combinedValues = `${displayId}:${companyId}`;

    try {
      this._screenId =
        await this.cryptoUtilsService.sha256base64hash(combinedValues);
    } catch (error) {
      console.log('Failed to generate hash:', error);
    }
  }

  private loadIntegrationsFromSettings() {
    //clear room IDs
    this.integrations.forEach((integration) => {
      integration.roomId = '';
    });

    const roomMapping = this.displayService.display?.settings?.roomMapping;

    // Return early if roomMapping doesn't exist or is not a string
    if (!roomMapping || typeof roomMapping !== 'string') {
      return;
    }

    try {
      // Parse JSON string to object
      const roomMappingObject = JSON.parse(roomMapping);

      // Validate that parsed result is an object
      if (
        roomMappingObject &&
        typeof roomMappingObject === 'object' &&
        !Array.isArray(roomMappingObject)
      ) {
        // Update existing integrations with room IDs from settings
        this.integrations.forEach((integration) => {
          if (roomMappingObject[integration.id]) {
            integration.roomId = roomMappingObject[integration.id];
          }
        });
      }
    } catch (error) {
      // Handle JSON parse errors gracefully
      console.warn('Failed to parse roomMapping from settings:', error);
    }
  }

  updateRoomMapping() {
    // Ensure settings object exists
    if (!this.displayService.display.settings) {
      this.displayService.display.settings = {};
    }

    // Create a single object with all room mappings, filtering out empty values
    const roomMappingObject = Object.fromEntries(
      this.integrations
        .filter((integration) => integration.roomId?.trim())
        .map((integration) => [integration.id, integration.roomId.trim()]),
    );

    // Store as JSON string (empty object becomes "{}" if no mappings exist)
    this.displayService.display.settings.roomMapping =
      JSON.stringify(roomMappingObject);
  }
}
